import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useRoomAccessStore } from '@/entities/call'
import { useSession } from '@/entities/session'
import { redirectToLogout } from '@/shared/api'
import { cn, deviceZoneDiffers, momentZone, zoneHint, zoneLabel } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { BrandLogo, EcosystemsLogo } from '@/shared/ui/brand'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { Menu } from '@/shared/ui/menu'
import { ToastViewport } from '@/shared/ui/toast'
import { navFor } from './nav-config'
import { ProductSwitcher } from './product-switcher'
import { SidebarNav } from './sidebar-nav'

// Показывать «Наверх» только когда шапка страницы давно за краем.
const SCROLL_TOP_THRESHOLD_PX = 600

// Подпись зоны: время встреч показано и вводится в зоне вашего устройства, и это
// сказано один раз в шапке, а не якорем в каждой строке. Точка-акцент — зона
// устройства разошлась с зоной организации, по которой считаются сутки отчётов;
// какая именно зона у организации, договаривает подсказка.
function ZoneBadge() {
  const tz = momentZone()
  const differs = deviceZoneDiffers()
  const hint = zoneHint(tz)
  return (
    <span
      title={hint}
      className="mr-1 hidden items-center gap-1 rounded-pill border border-border-default px-2 py-0.5 font-mono text-[11px] leading-4 text-text-tertiary sm:inline-flex"
    >
      {zoneLabel(tz)}
      {differs && <span className="text-accent">•</span>}
      {/* Скринридеру бейдж «Москва · UTC+3» сам по себе ничего не объясняет —
          рядом полная фраза, видимая только ему. */}
      <span className="sr-only">{hint}</span>
    </span>
  )
}

interface AppShellProps {
  // Палитра поиска (⌘K) живёт в pages/__root (widget→widget запрещён) —
  // шапка только просит её открыть.
  onOpenSearch: () => void
}

export function AppShell({ onOpenSearch }: AppShellProps) {
  const { isAuthenticated, session } = useSession()
  const navigate = useNavigate()
  const roomAccess = useRoomAccessStore((s) => s.roomAccess)
  const location = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const pathname = location.pathname
  // Сайдбар — только под сессией и вне полноэкранных контекстов:
  // звонок (/room/*) — фокус без навигации, гостевые (/guest/*) — нет сессии.
  const showSidebar = isAuthenticated && !pathname.startsWith('/room')
  // Контур решает и разделы, и наличие поиска: у заказчика витрина из одного
  // раздела, а поиск — не-цель фазы (`plan-customer/00-architecture § 6`).
  const kind = session?.kind ?? 'employee'
  const navItems = navFor(kind)
  const showSearch = isAuthenticated && kind !== 'customer'

  useEffect(() => {
    const el = mainRef.current
    if (!el) return
    const onScroll = () => setShowScrollTop(el.scrollTop > SCROLL_TOP_THRESHOLD_PX)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Логин — bare-страница: сама рисует полноэкранную раскладку без шелла.
  if (pathname.startsWith('/login')) {
    return (
      <>
        <Outlet />
        <ToastViewport />
      </>
    )
  }

  return (
    // App-frame: жёсткий viewport-кламп (h-dvh) — страница целиком никогда не
    // скроллится; прокрутка живёт внутри <main> (списки), а звонок занимает
    // ровно оставшуюся высоту без переполнения.
    <div className="flex h-dvh flex-col overflow-hidden bg-bg-base text-text-primary">
      <header className="relative flex h-14 shrink-0 items-center border-b border-border-default">
        {/* Бренд-зона = ширина сайдбара (когда он есть), её правая граница
            продолжает вертикаль сайдбара: слева от линии — экосистема, справа —
            текущий продукт. */}
        <div
          className={cn(
            'flex h-full shrink-0 items-center gap-3 px-4 sm:px-6',
            showSidebar && 'md:w-60 md:border-r md:border-border-default',
          )}
        >
          {showSidebar && (
            <button
              type="button"
              aria-label="Меню"
              onClick={() => setDrawerOpen(true)}
              className="shrink-0 text-text-secondary transition-colors hover:text-text-primary md:hidden"
            >
              <Icon name="menu" />
            </button>
          )}
          <Link
            to="/"
            aria-label="STIT Телемост — на главную"
            className="flex min-w-0 items-center rounded-control opacity-90 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-border-accent focus-visible:outline-none"
          >
            {/* На узком экране — только знак: полный логотип с дескриптором
                съедает место у свитчера и правой группы. */}
            <BrandLogo className="h-4 sm:hidden" />
            <EcosystemsLogo className="hidden h-5 sm:block" />
          </Link>
        </div>

        {/* Свитчер — первый элемент контентной колонки: отвечает «в каком сервисе
            я». Заказчику плитки сужены до продуктов его аккаунта; гостю без
            сессии экосистему не предлагаем — только имя продукта. */}
        <div className="flex h-full min-w-0 items-center px-2 sm:px-4">
          {isAuthenticated ? (
            <ProductSwitcher
              current="telemost"
              wordmark="ТЕЛЕМОСТ"
              {...(session?.kind === 'customer'
                ? { customerProducts: session.products ?? [] }
                : {})}
            />
          ) : (
            <span className="font-mono text-sm leading-none font-medium tracking-wide text-text-primary">
              ТЕЛЕМОСТ
            </span>
          )}
        </div>

        {/* Поиск — визуальное поле ввода; клик открывает ⌘K-палитру (реюз).
            Центрируется по ширине окна, а не по контентной колонке: длина имени
            продукта слева не должна двигать поле. Ширина — вьюпорт минус боковые
            зоны, чтобы поле не наезжало на свитчер и профиль; ниже xl лого
            экосистемы и чип ТЕЛЕМОСТ занимают левую половину — там поиск живёт
            иконкой в правой группе. */}
        {showSearch && (
          <button
            type="button"
            onClick={onOpenSearch}
            className="absolute left-1/2 hidden h-9 w-[calc(100vw-52rem)] max-w-sm -translate-x-1/2 items-center gap-2 rounded-control border border-border-default bg-bg-sunken px-3 text-sm text-text-tertiary transition-colors hover:border-border-strong hover:text-text-secondary focus:border-border-accent focus:outline-none xl:flex"
          >
            <Icon name="search" size={16} className="shrink-0" />
            <span className="flex-1 text-left">Поиск</span>
            <kbd className="hidden rounded-sm border border-border-default bg-bg-raised px-1 font-mono text-[11px] leading-4 text-text-tertiary lg:inline">
              ⌘K
            </kbd>
          </button>
        )}

        <div className="ml-auto flex h-full shrink-0 items-center gap-1 px-4 sm:px-6">
          <ZoneBadge />
          {showSearch && (
            <button
              type="button"
              aria-label="Поиск"
              onClick={onOpenSearch}
              className="inline-flex size-9 items-center justify-center rounded-control text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary xl:hidden"
            >
              <Icon name="search" size={20} />
            </button>
          )}
          {isAuthenticated && session && (
            <Menu
              align="end"
              aria-label="Меню пользователя"
              trigger={
                <span className="flex items-center gap-2 px-1">
                  <Avatar name={session.name ?? session.username ?? 'Пользователь'} size="sm" />
                  <span className="hidden max-w-40 truncate text-sm sm:block">
                    {session.name ?? session.username}
                  </span>
                  <Icon name="chevron-down" size={16} className="text-text-tertiary" />
                </span>
              }
              items={[
                {
                  label: 'Выйти',
                  icon: 'log-out',
                  danger: true,
                  // Логаут — у issuer'а своего контура (сотрудник — BFF
                  // id.stit.tech, заказчик — /auth/customer/logout хаба);
                  // своей ручки logout у telemost нет.
                  onSelect: () => redirectToLogout(),
                },
              ]}
            />
          )}
        </div>
      </header>
      {roomAccess && !pathname.startsWith('/room') && (
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border-default bg-bg-elevated px-6 py-2">
          <span className="flex items-center gap-2 text-sm text-text-primary">
            <span className="dot-pulse h-2 w-2 rounded-pill bg-accent" aria-hidden />
            Идёт встреча
          </span>
          <Button
            size="sm"
            onClick={() => void navigate({ to: '/room/$id', params: { id: roomAccess.meetingId } })}
          >
            Вернуться в звонок
          </Button>
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        {showSidebar && (
          <aside className="hidden w-60 shrink-0 flex-col border-r border-border-default p-3 md:flex">
            <SidebarNav items={navItems} />
          </aside>
        )}
        {/* scrollbar-gutter stable: появление скроллбара (длинный список) не
            сдвигает центрированный контент относительно короткого. */}
        <main
          ref={mainRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-gutter:stable]"
        >
          <Outlet />
        </main>
      </div>
      {showSidebar && drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Закрыть меню"
            className="absolute inset-0 bg-black/60"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="relative flex h-full w-64 flex-col gap-6 border-r border-border-default bg-bg-elevated p-3">
            <div className="px-2 pt-2">
              <Link
                to="/"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center text-text-primary"
              >
                <EcosystemsLogo className="h-5" />
              </Link>
            </div>
            <SidebarNav items={navItems} onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}
      {showScrollTop && (
        <button
          type="button"
          aria-label="Наверх"
          title="Наверх"
          onClick={() => mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed right-5 bottom-5 z-10 inline-flex size-11 items-center justify-center rounded-pill border border-border-strong bg-bg-raised text-text-secondary shadow-lg transition-colors hover:bg-bg-elevated hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
        >
          <Icon name="chevron-down" size={18} className="rotate-180" />
        </button>
      )}
      <ToastViewport />
    </div>
  )
}
